import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Descriptions, Tag, Typography, Button, Spin, Timeline, List, Space, Select,
  message, Modal, Form, Input, InputNumber, Popconfirm, Switch, Divider, Statistic,
} from 'antd';
import { ArrowLeft, Phone, Mail, MapPin, Pencil, Trash2 } from 'lucide-react';
import { leadsApi, inmovillaApi } from '../../services/api';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  NEW: 'blue', CONTACTED: 'cyan', QUALIFIED: 'green',
  NURTURING: 'orange', HOT: 'red', CONVERTED: 'purple', LOST: 'default',
};

const sourceLabels: Record<string, string> = {
  web: 'Web', referral: 'Referido', portal: 'Portal',
  walkin: 'Visita', phone: 'Teléfono', social: 'Redes sociales',
  WEB: 'Web (genérico)',
  WEB_CONTACT: 'Contacto web',
  WEB_COMPRADOR: 'Comprador web',
  valorador: 'Valorador online',
  'canal-denuncias': 'Canal de denuncias',
  'trabaja-con-nosotros': 'Trabaja con nosotros',
  'colabora-particular': 'Colabora como particular',
  'personal-shopper': 'Personal Shopper',
  'publicacion-portales': 'Publicación en portales',
  'unete-asociado': 'Únete — Asociado',
  'unete-prive': 'Únete — Privé',
  'unete-referencia': 'Únete — Referencia',
};

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [lead, setLead] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchLead = () => {
    if (!id) return;
    setLoading(true);
    leadsApi.get(id).then((r) => setLead(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchLead(); }, [id]);

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    try {
      const { data } = await leadsApi.update(id, { status: newStatus });
      setLead({ ...lead, ...data });
      message.success('Estado actualizado');
    } catch { message.error('Error al actualizar'); }
  };

  const openEdit = () => {
    form.setFieldsValue({
      firstName: lead.firstName,
      lastName: lead.lastName,
      email: lead.email,
      phone: lead.phone,
      source: lead.source,
      budget: lead.budget,
      preferredZone: lead.preferredZone,
      propertyType: lead.propertyType,
      bedrooms: lead.bedrooms,
      notes: lead.notes,
      score: lead.score,
      publishInmovilla: lead.publishInmovilla,
    });
    setEditOpen(true);
  };

  const handleEdit = async (values: any) => {
    if (!id) return;
    try {
      if (values.budget) values.budget = Number(values.budget);
      const { data } = await leadsApi.update(id, values);
      setLead({ ...lead, ...data });
      message.success('Lead actualizado');
      setEditOpen(false);
    } catch { message.error('Error al actualizar'); }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await leadsApi.delete(id);
      message.success('Lead eliminado');
      navigate('/leads');
    } catch { message.error('Error al eliminar'); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  if (!lead) return <Text type="danger">Lead no encontrado</Text>;

  return (
    <div>
      <Button type="text" icon={<ArrowLeft size={16} />} onClick={() => navigate('/leads')} style={{ marginBottom: 16 }}>
        Volver a leads
      </Button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>{lead.firstName} {lead.lastName || ''}</Title>
        <Space>
          <Select value={lead.status} onChange={handleStatusChange} style={{ width: 160 }}
            options={Object.keys(statusColors).map((s) => ({ value: s, label: s }))}
          />
          <Button icon={<Pencil size={14} />} onClick={openEdit}>Editar</Button>
          <Popconfirm title="¿Eliminar este lead?" description="Se eliminarán también sus oportunidades y reuniones" onConfirm={handleDelete} okText="Eliminar" cancelText="Cancelar" okButtonProps={{ danger: true }}>
            <Button danger icon={<Trash2 size={14} />}>Eliminar</Button>
          </Popconfirm>
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card title="Información del Lead">
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Email">
              {lead.email ? <a href={`mailto:${lead.email}`}><Mail size={12} /> {lead.email}</a> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Teléfono">
              {lead.phone ? <a href={`tel:${lead.phone}`}><Phone size={12} /> {lead.phone}</a> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Origen">{sourceLabels[lead.source] || lead.source}</Descriptions.Item>
            <Descriptions.Item label="Estado"><Tag color={statusColors[lead.status]}>{lead.status}</Tag></Descriptions.Item>
            <Descriptions.Item label="Score">
              {lead.score != null ? (
                <Tag color={lead.score >= 80 ? 'red' : lead.score >= 50 ? 'orange' : lead.score >= 25 ? 'blue' : 'default'}>
                  {lead.score}/100
                </Tag>
              ) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Presupuesto">{lead.budget ? `€${lead.budget.toLocaleString()}` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Zona"><MapPin size={12} /> {lead.preferredZone || '—'}</Descriptions.Item>
            <Descriptions.Item label="Tipo">{lead.propertyType || '—'}</Descriptions.Item>
            <Descriptions.Item label="Habitaciones">{lead.bedrooms || '—'}</Descriptions.Item>
            <Descriptions.Item label="Etiquetas">
              {lead.tags
                ? lead.tags.split(',').map((t: string) => <Tag key={t} color="geekblue">{t.trim()}</Tag>)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Asignado a">{lead.assignedTo?.name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Creado">{new Date(lead.createdAt).toLocaleDateString('es-ES')}</Descriptions.Item>
          </Descriptions>
          {lead.notes && <div style={{ marginTop: 12, padding: 12, background: '#fafafa', borderRadius: 8 }}><Text type="secondary">{lead.notes}</Text></div>}
        </Card>

        <Card title="Oportunidades">
          {lead.opportunities?.length > 0 ? (
            <List dataSource={lead.opportunities} renderItem={(opp: any) => (
              <List.Item actions={[<Button type="link" size="small" onClick={() => navigate(`/opportunities/${opp.id}`)}>Ver</Button>]}>
                <List.Item.Meta title={opp.title} description={`${opp.stage} · ${opp.value ? `€${opp.value.toLocaleString()}` : ''}`} />
              </List.Item>
            )} />
          ) : <Text type="secondary">Sin oportunidades</Text>}
        </Card>
      </div>

      {lead.source === 'valorador' && lead.metadata && (() => {
        const m = lead.metadata as any;
        const fmt = (n?: number) => n != null ? `${n.toLocaleString('es-ES')} €` : '—';
        const isPro = (m.ownerType || '').toLowerCase().includes('profesional') || (m.ownerType || '').toLowerCase() === 'profesional';
        const low   = m.estimatedLow  ?? (m.estimatedValue ? Math.round(m.estimatedValue * 0.94) : null);
        const high  = m.estimatedHigh ?? (m.estimatedValue ? Math.round(m.estimatedValue * 1.06) : null);
        const fast  = m.estimatedValue ? Math.round(m.estimatedValue * 0.90) : null;
        const opt   = m.estimatedValue ?? null;
        const out   = m.estimatedValue ? Math.round(m.estimatedValue * 1.12) : null;

        const chars: { label: string; value: any }[] = [
          { label: 'Dirección', value: m.address },
          { label: 'CP', value: m.postalCode },
          { label: 'Categoría', value: m.propCategory },
          { label: 'Familia', value: m.propFamily },
          { label: 'Tipo', value: lead.propertyType },
          { label: 'Superficie', value: m.size ? `${m.size} m²` : null },
          { label: 'Habitaciones', value: m.bedrooms ?? lead.bedrooms },
          { label: 'Baños', value: m.bathrooms ?? lead.bathrooms },
          { label: 'Planta', value: m.floor != null ? m.floor : null },
          { label: 'Ascensor', value: m.lift },
          { label: 'Orientación', value: m.orientation },
          { label: 'Exposición', value: m.exposure },
          { label: 'Parking', value: m.parking },
          { label: 'Trastero', value: m.storage },
          { label: 'Terraza', value: m.terrace ? `${m.terrace} m²` : null },
          { label: 'Piscina', value: m.pool },
          { label: 'Estado', value: m.state },
          { label: 'Año construcción', value: m.year },
          { label: 'Tipo calle', value: m.streetType },
          { label: 'Escaparate', value: m.shopfrontSize ? `${m.shopfrontSize} m` : null },
          { label: 'Esquina', value: m.cornerType },
          { label: 'Calidad terreno', value: m.landUrbanClass },
          { label: 'Calidad agrícola', value: m.landAgriQuality },
          { label: 'Licencia', value: m.landLicense },
          { label: 'Proyecto aprobado', value: m.landProject },
          { label: 'Agua', value: m.landWater },
          { label: 'Electricidad', value: m.landElectricity },
          { label: 'Acceso terreno', value: m.landAccess },
          { label: 'Vistas', value: m.landViews },
          { label: 'Pendiente', value: m.landSlope },
          { label: 'Protección', value: m.landProtection },
          { label: 'Perfil propietario', value: m.ownerType },
          { label: 'Plazo venta', value: m.saleTimeline },
          { label: '€/m² zona', value: m.priceM2 ? `${m.priceM2.toLocaleString('es-ES')} €/m²` : null },
          { label: 'Índice Swomey', value: m.swomeyIndex != null ? `${m.swomeyIndex}/100` : null },
        ].filter((c) => c.value != null && c.value !== '');

        return (
          <Card
            title={<span>📊 Valoración enviada</span>}
            style={{ marginBottom: 16 }}
            extra={<Tag color="purple">{isPro ? 'Profesional' : 'Particular'}</Tag>}
          >
            {/* Price result */}
            <div style={{ marginBottom: 20 }}>
              {isPro ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <Statistic title="Venta rápida" value={fast ?? 0} suffix="€" formatter={(v) => Number(v).toLocaleString('es-ES')} valueStyle={{ color: '#cf1322', fontSize: 18 }} />
                  <Statistic title="Precio óptimo" value={opt ?? 0} suffix="€" formatter={(v) => Number(v).toLocaleString('es-ES')} valueStyle={{ color: '#1677ff', fontSize: 18 }} />
                  <Statistic title="Fuera de mercado" value={out ?? 0} suffix="€" formatter={(v) => Number(v).toLocaleString('es-ES')} valueStyle={{ color: '#8c8c8c', fontSize: 18 }} />
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <Statistic title="Precio mínimo" value={low ?? 0} suffix="€" formatter={(v) => Number(v).toLocaleString('es-ES')} valueStyle={{ color: '#52c41a', fontSize: 18 }} />
                  <Statistic title="Precio máximo" value={high ?? 0} suffix="€" formatter={(v) => Number(v).toLocaleString('es-ES')} valueStyle={{ color: '#1677ff', fontSize: 18 }} />
                </div>
              )}
              {m.estimatedValue && (
                <div style={{ marginTop: 8 }}>
                  <Tag color="gold">Estimación base: {fmt(m.estimatedValue)}</Tag>
                  {m.swomeyIndex != null && (
                    <Tag color={m.swomeyIndex >= 80 ? 'red' : m.swomeyIndex >= 50 ? 'orange' : 'blue'}>
                      Índice Swomey {m.swomeyIndex}/100
                    </Tag>
                  )}
                </div>
              )}
            </div>

            <Divider style={{ margin: '12px 0' }}>Características de la propiedad</Divider>

            <Descriptions column={3} size="small" bordered>
              {chars.map((c) => (
                <Descriptions.Item key={c.label} label={c.label}>{String(c.value)}</Descriptions.Item>
              ))}
            </Descriptions>
          </Card>
        );
      })()}

      <Card title="Actividad">
        {lead.activities?.length > 0 ? (
          <Timeline items={lead.activities.map((a: any) => ({
            children: (
              <div>
                <Text style={{ fontSize: 13 }}>{a.description}</Text><br />
                <Text type="secondary" style={{ fontSize: 11 }}>{a.user?.name} · {new Date(a.createdAt).toLocaleString('es-ES')}</Text>
              </div>
            ),
          }))} />
        ) : <Text type="secondary">Sin actividad registrada</Text>}
      </Card>

      <Modal title="Editar Lead" open={editOpen} onCancel={() => setEditOpen(false)} onOk={() => form.submit()} okText="Guardar" width={600} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleEdit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="firstName" label="Nombre" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="lastName" label="Apellido"><Input /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="email" label="Email"><Input type="email" /></Form.Item>
            <Form.Item name="phone" label="Teléfono"><Input /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="source" label="Origen">
              <Select options={Object.entries(sourceLabels).map(([k, v]) => ({ value: k, label: v }))} />
            </Form.Item>
            <Form.Item name="budget" label="Presupuesto (€)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="preferredZone" label="Zona preferida"><Input /></Form.Item>
            <Form.Item name="propertyType" label="Tipo propiedad">
              <Select allowClear options={['villa', 'apartment', 'finca', 'penthouse', 'commercial'].map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))} />
            </Form.Item>
          </div>
          <Form.Item name="notes" label="Notas"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="score" label="Score (0-100)">
            <InputNumber style={{ width: '100%' }} min={0} max={100} placeholder="Puntuación del lead" />
          </Form.Item>
          <Divider style={{ margin: '12px 0' }} />
          <Form.Item name="publishInmovilla" label="Sincronizar con Inmovilla" valuePropName="checked"
            extra={lead.inmSyncStatus === 'ERROR' ? (
              <Text type="danger" style={{ fontSize: 12 }}>Error: {lead.inmSyncError}</Text>
            ) : lead.inmSyncStatus === 'SYNCED' ? (
              <Text type="success" style={{ fontSize: 12 }}>Sincronizado · ID: {lead.inmovillaId}</Text>
            ) : null}
          >
            <Switch checkedChildren="Sí" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
